import React                                from 'react';
import { Observe, watch, disposeOnUnmount } from 'rewire-core';
import {
  Theme,
  WithStyle,
  IActionMenuItem,
  FormView,
  IEditorField,
}                                            from 'rewire-ui';
import Divider                               from '@material-ui/core/Divider';
import Typography                            from '@material-ui/core/Typography';
import DeleteIcon                            from '@material-ui/icons/DeleteOutlined';
import hoursEarnedTypeDialogModel            from '../schedule/HoursEarnedTypeDialogModel';
import AddHoursEarnedTypeDialog              from './AddHoursEarnedTypeDialog';
import Loader                                from '../ui/Loader';
import InfoPanel                             from '../ui/InfoPanel';
import InfoPanelHeader                       from '../ui/InfoPanelHeader';
import InfoPanelContent                      from '../ui/InfoPanelContent';
import InfoPanelFooter                       from '../ui/InfoPanelFooter';
import ValidationRuleEditorInfoPanelModel    from './ValidationRuleEditorInfoPanelModel';
import { withStyles }                        from '../utility/styles';

const styles = (_theme: Theme) => ({
  fieldContainer: {
    width: '100%',
    marginBottom: '15px',
    '& > div': {
      display: 'flex',
    },
  },
  defaultMessage: {
    fontStyle: 'italic',
  },
  ruleName: {
    marginBottom: '10px',
  },
  ruleNameDivider: {
    marginLeft: '-20px',
    marginRight: '-20px',
    marginBottom: '10px',
  },
});

interface IValidationRuleEditorInfoPanelProps {
  classes?: any;
  model: ValidationRuleEditorInfoPanelModel;
}

type ValidationRuleEditorInfoPanelProps = WithStyle<ReturnType<typeof styles>, IValidationRuleEditorInfoPanelProps>;

class ValidationRuleEditorInfoPanel extends React.Component<ValidationRuleEditorInfoPanelProps> {
  optionsActionMenuItems: IActionMenuItem[];
  elevation: number;

  constructor(props: ValidationRuleEditorInfoPanelProps) {
    super(props);

    this.optionsActionMenuItems = [
      {name: 'deleteRule', title: 'Delete Rule', icon: DeleteIcon, onClick: this.handleDeleteRule, closeOnClick: true, disabled: () => !this.props.model.canDelete()},
    ];
    this.elevation = 4;
  }

  componentWillUnmount() {
    this.props.model.reset();
  }

  componentDidMount() {
    disposeOnUnmount(this, () => {
      watch(() => this.props.model.selectedRule, () => {
        this.props.model.load();
      });
    });
  }

  handleDeleteRule = () => {
    this.props.model.deleteRule();
  };

  renderRuleContent = React.memo((): JSX.Element => {
    const {classes} = this.props;

    return <Observe render={() => (
      <InfoPanelContent>
        <div className={classes.ruleName}>
          <Typography>{this.props.model.selectedRule!.name}</Typography>
        </div>
        <Divider className={classes.ruleNameDivider} />
        <FormView form={this.props.model.form} onSubmit={this.props.model.actionFn('save')}>
          {this.props.model.form.fields.map((field: IEditorField) =>
            <div key={`${this.props.model.selectedRule!.id}::${field.name}`} className={classes.fieldContainer}>
              <field.Editor />
            </div>
          )}
        </FormView>
        <AddHoursEarnedTypeDialog model={hoursEarnedTypeDialogModel} />
      </InfoPanelContent>
    )} />;
  });

  renderRuleInfo = React.memo((): JSX.Element => {
      const {model} = this.props;

      return (
        < >
        <Observe render={() => {
          const selectedRuleTableName = model.selectedRuleTable?.name;
          return (
            <InfoPanelHeader title={`${selectedRuleTableName} Rule`} optionsMenuTitle={`${selectedRuleTableName} Options`} optionsMenuTooltip={`${selectedRuleTableName} Options`} optionsItems={this.optionsActionMenuItems} />
          );
        }} />
        <this.renderRuleContent />
        <Observe render={() => <InfoPanelFooter isDisabled={model.isDisabled} actions={model.actions} /> } />
        </>
      );
  });

  render() {
    const {classes, model} = this.props;

    return (
      <InfoPanel>
        <Observe>{() => (
          <Loader loading={model.isLoading}>
            <Observe render={() => (
              model.selectedRule
                ? <this.renderRuleInfo />
                : <InfoPanelHeader
                    title={<Typography className={classes.defaultMessage}>Selecting a rule displays the rule&aposs properties in this area</Typography>}
                  />
            )} />
          </Loader>
        )}</Observe>
      </InfoPanel>
    );
  }
}

export default withStyles<IValidationRuleEditorInfoPanelProps>(styles, ValidationRuleEditorInfoPanel);
